// Bygger en graf av Bas-processen utifrån dokumentens buildsOn/feeds.
import { PROCESS_PHASES, PROCESS_STEPS, type ProcessPhase, type ProcessStepDef } from "./constants";
import { DOC_EXPLANATIONS, getExplanation } from "./explanations";
import { ROLE_VIEWS, type ProjectRole } from "./roleViews";

export interface GraphNode {
  doc: string;
  step: ProcessStepDef;
  phase: ProcessPhase;
  buildsOn: string[];
  feeds: string[];
}

export interface GraphEdge {
  from: string;
  to: string;
}

const VALID = new Set(PROCESS_STEPS.map((s) => s.docNumber));

export const GRAPH_NODES: GraphNode[] = PROCESS_STEPS.map((step) => {
  const exp = getExplanation(step.docNumber);
  return {
    doc: step.docNumber,
    step,
    phase: step.phase,
    buildsOn: (exp?.buildsOn ?? []).filter((d) => VALID.has(d)),
    feeds: (exp?.feeds ?? []).filter((d) => VALID.has(d)),
  };
});

export const NODE_BY_DOC: Record<string, GraphNode> = Object.fromEntries(
  GRAPH_NODES.map((n) => [n.doc, n])
);

/** Unika kanter (from -> to), härledda från både feeds och buildsOn. */
export const GRAPH_EDGES: GraphEdge[] = (() => {
  const seen = new Set<string>();
  const edges: GraphEdge[] = [];
  const add = (from: string, to: string) => {
    if (from === to) return;
    const key = `${from}->${to}`;
    if (seen.has(key)) return;
    seen.add(key);
    edges.push({ from, to });
  };
  GRAPH_NODES.forEach((n) => {
    n.feeds.forEach((t) => add(n.doc, t));
    n.buildsOn.forEach((s) => add(s, n.doc));
  });
  return edges;
})();

/** Noder per fas, i den ordning faserna definieras. */
export const NODES_BY_PHASE = PROCESS_PHASES.map((phase) => ({
  phase,
  nodes: GRAPH_NODES.filter((n) => n.phase === phase.key),
}));

const traverse = (start: string, direction: "up" | "down"): Set<string> => {
  const result = new Set<string>();
  const queue: string[] = [start];
  while (queue.length) {
    const cur = queue.shift()!;
    const node = NODE_BY_DOC[cur];
    if (!node) continue;
    const next = direction === "up" ? node.buildsOn : node.feeds;
    next.forEach((d) => {
      if (!result.has(d) && d !== start) {
        result.add(d);
        queue.push(d);
      }
    });
  }
  return result;
};

export interface ChainHighlight {
  upstream: Set<string>;
  downstream: Set<string>;
}

/** Allt som (indirekt) matar in i dokumentet respektive tar emot från det. */
export const getChain = (doc: string): ChainHighlight => ({
  upstream: traverse(doc, "up"),
  downstream: traverse(doc, "down"),
});

export const docTitle = (doc: string) => NODE_BY_DOC[doc]?.step.title ?? `Dok ${doc}`;

export const hasExplanation = (doc: string) => Boolean(DOC_EXPLANATIONS[doc]);

export type RoleFilter = "alla" | "Bas-P" | "Bas-U" | "Byggherre" | "Projektör" | "Entreprenör";

export type RoleRelation = "owns" | "contributes" | null;

/**
 * Rollens relation till ett dokument: äger, bidrar till eller inget alls.
 * Bygger på rollvyerna så att t.ex. Projektör och Entreprenör får en
 * meningsfull vy även när de inte står som ägare i processlistan.
 */
export const getRoleRelation = (doc: string, owner: string, filter: RoleFilter): RoleRelation => {
  if (filter === "alla") return "owns";
  if (owner.toLowerCase().includes(filter.toLowerCase())) return "owns";
  const view = ROLE_VIEWS[filter as ProjectRole];
  if (!view) return null;
  if (view.owns.includes(doc)) return "owns";
  if (view.contributes.includes(doc)) return "contributes";
  return null;
};

export const matchesRole = (owner: string, filter: RoleFilter) =>
  filter === "alla" || owner.toLowerCase().includes(filter.toLowerCase());

/* -------------------------------------------------------------------------
 * Simbanor: varje dokument hamnar i exakt en bana (ägaren) och en kolumn (skedet).
 * Det gör att man ser vem som äger vad utan att behöva följa pilar.
 * ---------------------------------------------------------------------- */

export interface LaneDef {
  key: string;
  label: string;
  /** Kort förklaring av vad rollen ansvarar för i processen. */
  hint: string;
  owners: ProcessStepDef["owner"][];
}

export const PROCESS_LANES: LaneDef[] = [
  {
    key: "byggherre",
    label: "Byggherre",
    hint: "Yttersta ansvaret – utser Bas-P och Bas-U",
    owners: ["Byggherre"],
  },
  {
    key: "projektor",
    label: "Projektör",
    hint: "Projekterar bort risker och redovisar de som blir kvar",
    owners: ["Projektör"],
  },
  {
    key: "basp",
    label: "Bas-P",
    hint: "Samordnar arbetsmiljön under planering och projektering",
    owners: ["Bas-P"],
  },
  {
    key: "gemensamt",
    label: "Bas-P och Bas-U tillsammans",
    hint: "Överlämning och löpande samordning mellan skedena",
    owners: ["Bas-P/Bas-U"],
  },
  {
    key: "basu",
    label: "Bas-U",
    hint: "Samordnar arbetsmiljön ute på bygget",
    owners: ["Bas-U"],
  },
];

export interface LaneCell {
  phase: ProcessPhase;
  nodes: GraphNode[];
}

export interface LaneRow {
  lane: LaneDef;
  cells: LaneCell[];
  total: number;
}

/** Noder grupperade som roll-rader × fas-kolumner. Tomma banor filtreras bort. */
export const getLaneRows = (docs?: Set<string>): LaneRow[] =>
  PROCESS_LANES.map((lane) => {
    const laneNodes = GRAPH_NODES.filter(
      (n) => lane.owners.includes(n.step.owner) && (!docs || docs.has(n.doc))
    );
    return {
      lane,
      total: laneNodes.length,
      cells: PROCESS_PHASES.map((p) => ({
        phase: p.key,
        nodes: laneNodes.filter((n) => n.phase === p.key),
      })),
    };
  }).filter((row) => row.total > 0);

